import { ChangeDetectorRef, Component, ViewChild, ElementRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import {
  Event,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RoutesRecognized,
  RouterLink,
  RouterOutlet
} from '@angular/router';

import { TranslateService, TranslateModule } from '@ngx-translate/core';

import { AppDataService } from 'src/app/services/data/app-data.service';
import { AclService } from './services/acl.service';
import { EnumAcl } from '@simplyfly/shared';

import { environment } from '../environments/environment';
import { IUserGroup } from '@simplyfly/shared';
import {
  faBuilding,
  faCalendar,
  faChartLine,
  faChevronLeft,
  faChevronRight,
  faCogs,
  faDollar,
  faFileInvoice,
  faHome,
  faPlane,
  faPlaneDeparture,
  faReceipt,
  faSearch,
  faUsers,
  faUserTie
} from '@fortawesome/free-solid-svg-icons';
import { IMenuLink, SidebarMenuComponent } from './components/sidebar-menu/sidebar-menu.component';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  mergeMap,
  of,
  Subject,
  Subscription
} from 'rxjs';
import { IEnquiry } from '@simplyfly/shared';
import { getDisplayedEnquiryRefTitle } from './interfaces/enquiry.helpers';
import { AlgoliaService } from './services/algolia.service';
import { LoaderService } from './services/loader/loader.service';
import { IInvoice } from '@simplyfly/shared';
import { formatPrice } from './misc.utils';
import { IAirport } from '@simplyfly/shared';
import { IClient } from '@simplyfly/shared';
import { PipedriveService } from './services/pipedrive.service';
import { IPipedriveOrganization } from '@simplyfly/shared';
import { ISupplier } from '@simplyfly/shared';
import { IUser } from '@simplyfly/shared';
import { EnumEnquiryType } from '@simplyfly/shared';
import { UserEventLogsService } from './services/user-event-logs/user-event-logs.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { preferLiteralEnquiryReferenceMatches } from './app-search.helpers';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { HeaderMenuComponent } from './components/header-menu/header-menu.component';
import { AlertComponent } from './components/alert/alert.component';
import { LoaderComponent } from './components/loader/loader.component';

enum EnumSearchKey {
  enquiries = 'enquiries',
  invoices = 'invoices',
  airports = 'airports',
  clients = 'clients',
  suppliers = 'suppliers'
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
      RouterLink,
      FaIconComponent,
      FormsModule,
      SidebarMenuComponent,
      HeaderMenuComponent,
      RouterOutlet,
      AlertComponent,
      LoaderComponent,
      TranslateModule
    ]
})
export class AppComponent implements OnDestroy {
  @ViewChild('menuCloseBtn', { static: false }) menuCloseBtnElement: ElementRef;

  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faSearch = faSearch;

  getDisplayedEnquiryRefTitle = getDisplayedEnquiryRefTitle;
  formatPrice = formatPrice;

  EnumSearchKey = EnumSearchKey;
  EnumAcl = EnumAcl;

  displaySidebar = false;
  displayTopMenu = false;
  menuCollapsed = false;
  menuCollapsedAnimation = false;
  currentRoute = '/';

  hideLoading = false;
  currentUrl: string;
  isLogged = false;
  isLoginPage = false;

  menuLinks: IMenuLink[] = [];

  isStaging = false;
  currentUser: IUser | null = null;

  searchKeyUp: Subject<string> = new Subject<string>();
  searchQuery = '';
  searchOptions: {
    total: number;
    enquiries: IEnquiry[];
    invoices: IInvoice[];
    airports: IAirport[];
    clients: IPipedriveOrganization[];
    suppliers: ISupplier[];
  } = {
    total: 0,
    enquiries: [],
    invoices: [],
    airports: [],
    clients: [],
    suppliers: []
  };
  searchLoading: { [searchKey: string]: boolean } = {
    enquiries: true,
    invoices: true,
    airports: true,
    clients: true,
    suppliers: true
  };
  searchErrors: { [searchKey: string]: boolean } = {};
  private sidebarSearchRequestId = 0;

  searchKeys: {
    title: string;
    value: EnumSearchKey;
    count: number;
    exactMatch: boolean;
  }[] = [
    {
      title: 'ENQUIRIES.PLURAL',
      value: EnumSearchKey.enquiries,
      count: 0,
      exactMatch: false
    },
    {
      title: 'INVOICE.PLURAL',
      value: EnumSearchKey.invoices,
      count: 0,
      exactMatch: false
    },
    {
      title: 'CLIENTS.PLURAL',
      value: EnumSearchKey.clients,
      count: 0,
      exactMatch: false
    },
    {
      title: 'SUPPLIERS.PLURAL',
      value: EnumSearchKey.suppliers,
      count: 0,
      exactMatch: false
    },
    {
      title: 'AIRPORTS.PLURAL',
      value: EnumSearchKey.airports,
      count: 0,
      exactMatch: false
    }
  ];

  private subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private appDataService: AppDataService,
    private aclService: AclService,
    private changeDetectorRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private algoliaService: AlgoliaService,
    private loaderService: LoaderService,
    private pipedriveService: PipedriveService,
    private userEventLogsService: UserEventLogsService
  ) {
    if (environment.environmentType === 'staging') {
      this.isStaging = true;
    }

    // this language will be used as a fallback when a translation isn't found in the current language
    this.translateService.addLangs(['en', 'fr']);
    this.translateService.setDefaultLang('en');

    const browserLang: string | undefined = this.translateService.getBrowserLang();
    const currentLang: string | undefined = browserLang?.match(/en|fr/) ? browserLang : 'en';
    this.translateService.use(currentLang);

    this.currentRoute = typeof window !== 'undefined' ? window.location.pathname : '/';
    this.updatedCurrentUrl();

    this.refreshMenuLinks();

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        // Show progress spinner or progress bar
        // console.log('Route change detected');
      }

      if (event instanceof NavigationEnd) {
        // Hide progress spinner or progress bar
        this.currentRoute = event.url;
        this.userEventLogsService.trackNavigation();
      }

      if (event instanceof NavigationError) {
        // Hide progress spinner or progress bar

        // Present error to user
        console.log(event.error);
      }

      this.updatedCurrentUrl();
      this.changeDetectorRef.markForCheck();
    });

    this.router.events.subscribe((event: Event) => {
      if (event instanceof RoutesRecognized) {
        this.currentUrl = event.url;

        // An event triggered when the Router parses the URL and the routes are recognized.
        this.displaySidebar =
          (this.currentUrl.match('admin') === null && this.currentUrl.match('public') === null) ||
          (!this.isLogged && this.currentUrl.match('public') !== null);
        this.changeDetectorRef.markForCheck();
      }
    });
    this.authService.isLoggedObservable.subscribe((isLogged: boolean) => {
      if (isLogged != null) {
        this.isLogged = isLogged;

        this.hideLoading = true;

        this.checkAndRedirectToAdmin(isLogged);

        this.refreshMenuLinks();
        this.changeDetectorRef.markForCheck();
      }
    });
    this.authService.userGroupObservable.subscribe((userGroup: IUserGroup | null) => {
      if (userGroup) {
        this.refreshMenuLinks();
      }
      this.changeDetectorRef.markForCheck();
    });
    this.authService.userObservable.subscribe((user: IUser | null) => {
      this.currentUser = user;

      if (user?.language) {
        this.translateService.use(user.language);
      }
      this.changeDetectorRef.markForCheck();
    });

    this.subscriptions.add(
      this.searchKeyUp
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          mergeMap((search: string) => of(search).pipe(delay(500)))
        )
        .subscribe(() => {
          void this.search();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  hasAclAccess(id: EnumAcl): boolean {
    return this.aclService.hasAclAccess(id);
  }

  checkAndRedirectToAdmin(isLogged: boolean): void {
    if (this.currentUrl?.match('set-password')) {
      return;
    }

    if (this.currentUrl) {
      if (!isLogged && this.currentUrl.match('admin')) {
        void this.router.navigate(['/login']);
      } else if (isLogged && !this.currentUrl.match('admin') && !this.currentUrl.match('public')) {
        void this.router.navigate(['/admin']);
      }
    } else {
      setTimeout(() => {
        this.checkAndRedirectToAdmin(isLogged);
      }, 300);
    }
  }

  openMenuItem(menuItem: { title: string; url: string | false }): void {
    if (menuItem.url) {
      if (window.innerWidth < 992) {
        this.menuCloseBtnElement.nativeElement.click();
      }

      this.router.navigate([menuItem.url]);
    }
  }

  updatedCurrentUrl(): void {
    this.displaySidebar = false;
    this.displayTopMenu = true;
    this.isLoginPage = false;

    if (this.currentRoute.match('/admin') || this.currentRoute.match('/public')) {
      this.displaySidebar = true;
    }

    const urlsToHideTopMenu: string[] = ['/login', '/password-forgotten', '/set-password'];

    if (this.currentRoute === '/') {
      this.displayTopMenu = false;
      this.isLoginPage = true;
    }

    for (const url of urlsToHideTopMenu) {
      if (this.currentRoute.match(url)) {
        this.displayTopMenu = false;
        this.isLoginPage = true;
        break;
      }
    }
  }

  menuExpand(): void {
    if (window.innerWidth >= 992) {
      this.menuCollapsedAnimation = true;
      this.menuCollapsed = !this.menuCollapsed;

      setTimeout(() => {
        this.menuCollapsedAnimation = false;
        this.changeDetectorRef.markForCheck();
      }, 1000);
    }
  }

  refreshMenuLinks(): void {
    this.menuLinks = [];

    this.menuLinks.push({
      iconLeft: faHome,
      text: 'GENERAL.HOME',
      href: '/admin'
    });
    if (this.aclService.hasAclAccess(EnumAcl.flightsCalendarDashboard)) {
      this.menuLinks.push({
        iconLeft: faCalendar,
        text: 'FLIGHTS_CALENDAR.TITLE',
        href: '/admin/flight-calendar'
      });
    }
    if (this.aclService.hasAclAccess(EnumAcl.usersStatistics)) {
      this.menuLinks.push({
        iconLeft: faChartLine,
        text: 'REPORTS.PLURAL',
        href: '/admin/statistics'
      });
    } else if (this.aclService.hasAclAccess(EnumAcl.usersUserOwnStatistics)) {
      this.menuLinks.push({
        iconLeft: faChartLine,
        text: 'USERS.USER_OWN_STATISTICS',
        href: '/admin/statistics/current-user'
      });
    }
    if (this.aclService.hasAclAccess(EnumAcl.enquiryPaymentsSummaryDisplay)) {
      this.menuLinks.push({
        iconLeft: faDollar,
        text: 'ENQUIRY_PAYMENTS_SUMMARY.PLURAL',
        href: '/admin/finance'
      });
    } else if (this.aclService.hasAclAccess(EnumAcl.enquiryPaymentsSummaryOwnedEnquiries)) {
      this.menuLinks.push({
        iconLeft: faDollar,
        text: 'ENQUIRY_PAYMENTS_SUMMARY.PLURAL',
        href: '/admin/finance'
      });
    }
    if (this.aclService.hasAclAccess(EnumAcl.clientsList)) {
      this.menuLinks.push({
        iconLeft: faUserTie,
        text: 'CLIENTS.PLURAL',
        href: '/admin/clients'
      });
    }
    if (this.aclService.hasAclAccess(EnumAcl.suppliersList)) {
      this.menuLinks.push({ iconLeft: faBuilding, text: 'SUPPLIERS.PLURAL', href: '/admin/suppliers' });
    }
    if (this.aclService.hasAclAccess(EnumAcl.airlinesList)) {
      this.menuLinks.push({
        iconLeft: faBuilding,
        text: 'AIRLINES.PLURAL',
        href: '/admin/airlines'
      });
    }
    if (this.aclService.hasAclAccess(EnumAcl.usersList)) {
      this.menuLinks.push({
        iconLeft: faUsers,
        text: 'USERS.PLURAL',
        href: '/admin/users'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.invoicesSidebarMenu)) {
      this.menuLinks.push({
        iconLeft: faFileInvoice,
        text: 'INVOICE.PLURAL',
        href: '/admin/invoices'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.encaissementsList)) {
      this.menuLinks.push({
        iconLeft: faReceipt,
        text: 'ENCAISSEMENTS.PLURAL',
        href: '/admin/encaissements'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.airportsList)) {
      this.menuLinks.push({
        iconLeft: faPlaneDeparture,
        text: 'AIRPORTS.PLURAL',
        href: '/admin/airports/'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.lieuxDitsList)) {
      this.menuLinks.push({
        iconLeft: faPlaneDeparture,
        text: 'LIEUX_DITS.PLURAL',
        href: '/admin/lieux-dits'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.aircraftModelsList)) {
      this.menuLinks.push({
        iconLeft: faPlane,
        text: 'AIRCRAFT_MODELS.PLURAL',
        href: '/admin/aircraft-models'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.aircraftsList)) {
      this.menuLinks.push({
        iconLeft: faPlane,
        text: 'AIRCRAFTS.PLURAL',
        href: '/admin/aircrafts'
      });
    }

    const settingsChildrenLinks: IMenuLink[] = [];

    if (this.aclService.hasAclAccess(EnumAcl.simulateurAircraftModelsList)) {
      settingsChildrenLinks.push({
        text: 'SIMULATOR.AIRCRAFT_MODELS',
        href: '/admin/simulateur-aircraft-models'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.emailTemplatesList)) {
      settingsChildrenLinks.push({
        text: 'EMAIL_TEMPLATES.PLURAL',
        href: '/admin/email-templates'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.userGroupsList)) {
      settingsChildrenLinks.push({
        text: 'USER_GROUPS.PLURAL',
        href: '/admin/user-groups'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.encaissementTypesList)) {
      settingsChildrenLinks.push({
        text: 'ENCAISSEMENT_TYPES.PLURAL',
        href: '/admin/encaissement-types'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.bankAccountsList)) {
      settingsChildrenLinks.push({
        text: 'BANK_ACCOUNTS.PLURAL',
        href: '/admin/bank-accounts'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.flightBriefCateringsList)) {
      settingsChildrenLinks.push({
        text: 'FLIGHT_BRIEF_CATERINGS.PLURAL',
        href: '/admin/flight-brief-caterings'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.crawlerUrlsList)) {
      settingsChildrenLinks.push({
        text: 'CRAWLER_URLS.PLURAL',
        href: '/admin/crawler-urls'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.backupsList)) {
      settingsChildrenLinks.push({
        text: 'BACKUPS.PLURAL',
        href: '/admin/backups'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.countriesList)) {
      settingsChildrenLinks.push({
        text: 'COUNTRIES.PLURAL',
        href: '/admin/countries'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.flightChecklistTemplatesList)) {
      settingsChildrenLinks.push({
        text: 'FLIGHT_CHECKLIST_TEMPLATES.PLURAL',
        href: '/admin/flight-checklist-templates'
      });
    }

    if (this.aclService.hasAclAccess(EnumAcl.settingsPipedrive)) {
      settingsChildrenLinks.push({
        text: 'GENERAL.SETTINGS',
        href: '/admin/settings'
      });
    }

    if (settingsChildrenLinks.length) {
      if (settingsChildrenLinks.length === 1) {
        this.menuLinks.push(Object.assign({ children: [] }, settingsChildrenLinks[0]));
      } else {
        this.menuLinks.push({
          iconLeft: faCogs,
          text: 'GENERAL.SETTINGS',
          children: settingsChildrenLinks
        });
      }
    }
  }

  async search(): Promise<void> {
    const query = this.searchQuery.trim();
    const requestId = ++this.sidebarSearchRequestId;
    const canSearchInvoices = this.hasAclAccess(EnumAcl.invoicesSidebarMenu);
    const canSearchSuppliers = this.hasAclAccess(EnumAcl.suppliersList);

    this.searchOptions.total = 0;
    for (const searchKey of this.searchKeys) {
      searchKey.count = 0;
      searchKey.exactMatch = false;
      this.searchLoading[searchKey.value] = !!query;
      this.searchErrors[searchKey.value] = false;
      this.searchOptions[searchKey.value] = [];
    }
    if (!query) {
      this.changeDetectorRef.markForCheck();
      return;
    }

    const isCurrent = () => requestId === this.sidebarSearchRequestId && query === this.searchQuery.trim();
    const load = async <T>(
      key: EnumSearchKey,
      request: () => Promise<T[]>,
      assign: (items: T[]) => void
    ): Promise<void> => {
      try {
        const items = await request();
        if (isCurrent()) assign((items || []).slice(0, 5));
      } catch (error) {
        if (isCurrent()) {
          console.error(`Unable to search ${key}`, error);
          this.searchErrors[key] = key === EnumSearchKey.suppliers;
        }
      } finally {
        if (isCurrent()) {
          this.searchLoading[key] = false;
          this.changeDetectorRef.markForCheck();
        }
      }
    };

    await Promise.all([
      load(EnumSearchKey.enquiries, async () => {
        const enquiries = await this.algoliaService.searchEnquiries(query);
        const allowed = new Set<EnumEnquiryType>(this.currentUser?.enquiryTypes || []);
        return preferLiteralEnquiryReferenceMatches(
          enquiries.filter(enquiry => allowed.has(enquiry.type)), query
        );
      }, items => this.searchOptions.enquiries = items),
      load(EnumSearchKey.invoices,
        () => canSearchInvoices ? this.algoliaService.searchInvoices(query, ['ref'], []) : Promise.resolve([]),
        items => this.searchOptions.invoices = items),
      load(EnumSearchKey.airports,
        () => this.algoliaService.searchAirports(query),
        items => this.searchOptions.airports = items),
      load(EnumSearchKey.clients,
        () => this.pipedriveService.searchOrganization(query),
        items => this.searchOptions.clients = items),
      load(EnumSearchKey.suppliers,
        async () => canSearchSuppliers
          ? (await this.algoliaService.searchSuppliers(query, 0, 5)).suppliers
          : [],
        items => this.searchOptions.suppliers = items)
    ]);
    if (!isCurrent()) return;

    const normalizedQuery = query.toLocaleLowerCase();
    for (const searchKey of this.searchKeys) {
      switch (searchKey.value) {
        case EnumSearchKey.enquiries:
          searchKey.count = this.searchOptions.enquiries.length;
          searchKey.exactMatch = this.searchOptions.enquiries.some(enquiry =>
            getDisplayedEnquiryRefTitle(enquiry, 'refEnquiry').toLocaleLowerCase() === normalizedQuery ||
            !!enquiry.refContractTitle &&
              getDisplayedEnquiryRefTitle(enquiry, 'refContract').toLocaleLowerCase() === normalizedQuery);
          break;
        case EnumSearchKey.invoices:
          searchKey.count = this.searchOptions.invoices.length;
          searchKey.exactMatch = this.searchOptions.invoices.some(invoice =>
            invoice.ref.toLocaleLowerCase() === normalizedQuery);
          break;
        case EnumSearchKey.airports:
          searchKey.count = this.searchOptions.airports.length;
          searchKey.exactMatch = this.searchOptions.airports.some(airport =>
            airport.iataCode?.toLocaleLowerCase() === normalizedQuery ||
            airport.icaoCode?.toLocaleLowerCase() === normalizedQuery);
          break;
        case EnumSearchKey.clients:
          searchKey.count = this.searchOptions.clients.length;
          searchKey.exactMatch = this.searchOptions.clients.some(client =>
            client.name.toLocaleLowerCase() === normalizedQuery);
          break;
        case EnumSearchKey.suppliers:
          searchKey.count = this.searchOptions.suppliers.length;
          searchKey.exactMatch = this.searchOptions.suppliers.some(supplier =>
            supplier.name.toLocaleLowerCase() === normalizedQuery);
          break;
      }
      this.searchOptions.total += searchKey.count;
    }

    const originalOrder = [
      EnumSearchKey.enquiries, EnumSearchKey.invoices, EnumSearchKey.clients,
      EnumSearchKey.suppliers, EnumSearchKey.airports
    ];
    this.searchKeys.sort((left, right) =>
      Number(right.exactMatch) - Number(left.exactMatch) ||
      Number(right.value === EnumSearchKey.enquiries && right.count > 0) -
        Number(left.value === EnumSearchKey.enquiries && left.count > 0) ||
      right.count - left.count ||
      originalOrder.indexOf(left.value) - originalOrder.indexOf(right.value));
    this.changeDetectorRef.markForCheck();
  }

  getSearchResultCount(searchKey: EnumSearchKey): number {
    switch (searchKey) {
      case EnumSearchKey.enquiries:
        return this.searchOptions.enquiries.length;
      case EnumSearchKey.invoices:
        return this.searchOptions.invoices.length;
      case EnumSearchKey.airports:
        return this.searchOptions.airports.length;
      case EnumSearchKey.clients:
        return this.searchOptions.clients.length;
      case EnumSearchKey.suppliers:
        return this.searchOptions.suppliers.length;
    }
  }

  openItem(
    searchKey: EnumSearchKey,
    item: IEnquiry | IInvoice | IAirport | IPipedriveOrganization | ISupplier
  ): void {
    this.searchQuery = '';

    let url: string | null = null;
    switch (searchKey) {
      case EnumSearchKey.enquiries:
        url = '/admin/enquiries/' + item.id;
        break;
      case EnumSearchKey.invoices:
        url = '/admin/invoices/' + item.id;
        break;
      case EnumSearchKey.airports:
        url = '/admin/airports/' + item.id;
        break;
      case EnumSearchKey.clients:
        url = '/admin/clients/' + item.id;
        break;
      case EnumSearchKey.suppliers:
        url = '/admin/suppliers/' + item.id;
        break;
    }

    if (url) {
      this.loaderService.presentLoader();

      this.router.navigateByUrl(url);

      setTimeout(() => {
        this.loaderService.hideLoaderOnSuccess();
      }, 1000);
    }
  }
}
